import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import uniqBy from 'lodash.uniqby';

import { useJobPositionsService } from '@restworld/data-services';
import { useResponsiveBreakPoints } from '@restworld/utils-common';

import {
  jobListParamsAtom,
  jobLocationPositionListAtom,
  jobLocationPositionListLoadingAtom,
  jobPositionTotalCountAtom,
  jobListHasLimitReachedAtom,
  jobListKeyAtom,
  hasMapViewAtom,
} from '../job-explorer-list.atoms';

export const useJobLocationPositionList = () => {
  const hasMapView = useAtomValue(hasMapViewAtom);
  const { isDesktop } = useResponsiveBreakPoints();
  const [enabled, setEnabled] = useState(false);
  const jobPositionService = useJobPositionsService();
  const setJobLocationPositionList = useSetAtom(jobLocationPositionListAtom);
  const setJobPositionTotalCount = useSetAtom(jobPositionTotalCountAtom);
  const setJobLocationPositionListLoading = useSetAtom(jobLocationPositionListLoadingAtom);
  const setJobListHasLimitReached = useSetAtom(jobListHasLimitReachedAtom);

  const jobListParams = useAtomValue(jobListParamsAtom);
  const queryKey = useAtomValue(jobListKeyAtom);

  const queryFn = useCallback(
    async ({ signal }) => {
      setJobLocationPositionListLoading(true);
      const data = (await jobPositionService.getJobLocationPositionList(jobListParams, { signal })).data.data
        ?.explore_job_positions;

      setJobLocationPositionListLoading(false);
      setJobLocationPositionList((prev) => {
        if (jobListParams.offset > 0) return uniqBy([...prev, ...(data?.data ?? [])], 'id');
        return [...(data?.data ?? [])];
      });

      setJobPositionTotalCount(data?.hits ?? 0);
      setJobListHasLimitReached((data?.data.length ?? 0) === 0);
      return data ?? [];
    },
    [
      jobListParams,
      jobPositionService,
      setJobListHasLimitReached,
      setJobLocationPositionList,
      setJobLocationPositionListLoading,
      setJobPositionTotalCount,
    ]
  );

  useEffect(() => {
    const timeout = setTimeout(() => setEnabled(true), 450);
    return () => clearTimeout(timeout);
  }, []);

  return useQuery({
    queryKey,
    queryFn,
    placeholderData: (previousData) => previousData,
    enabled: isDesktop ? enabled : !hasMapView,
    staleTime: 0,
  });
};
